/**
 * Deleting a device, with the consequence stated first.
 *
 * A dialog that says "delete?" is one keystroke between an operator and a
 * client's recorded history, and everybody clicks through it. This one loads what
 * would actually be destroyed, says it in days rather than segment counts, and
 * requires the device name to be typed.
 *
 * The typing is not friction for its own sake — it is the smallest control that
 * cannot be satisfied without having read what is about to happen.
 */

import { useEffect, useState } from 'react';

import { ApiError, deleteDevice, fetchDeletionImpact, type DeletionImpact } from './api.ts';
import { describeLoss } from './auditViewModel.ts';

export function DeleteDevice({
  deviceId,
  deviceName,
  onDeleted,
}: {
  deviceId: string;
  deviceName: string;
  onDeleted: () => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [impact, setImpact] = useState<DeletionImpact | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    setImpact(null);
    void fetchDeletionImpact('devices', deviceId).then(
      (result) => {
        if (!cancelled) setImpact(result);
      },
      () => {
        /*
         * The count could not be read. The delete is still offered — refusing to
         * let someone remove a device because a count failed would be worse —
         * but without a number the confirmation says so rather than implying
         * there is nothing to lose.
         */
        if (!cancelled) setImpact(null);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [open, deviceId]);

  async function remove(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await deleteDevice(deviceId, typed.trim());
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete the device.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="btn btn--sm btn--danger" onClick={() => setOpen(true)}>
        Delete device
      </button>
    );
  }

  const loss = impact ? describeLoss(impact) : null;
  const matches = typed.trim() === deviceName;

  return (
    <div className="destroy">
      <h4>Delete {deviceName}?</h4>

      {impact === null ? (
        <p className="form__hint">Checking what this would destroy…</p>
      ) : loss ? (
        <p className="notice notice--error">{loss} This cannot be undone.</p>
      ) : (
        <p className="form__hint">
          Nothing has been recorded from this device yet, so no footage is lost.
        </p>
      )}

      <div className="field">
        <label htmlFor={`confirm-${deviceId}`}>
          Type <strong>{deviceName}</strong> to confirm
        </label>
        <input
          id={`confirm-${deviceId}`}
          value={typed}
          autoComplete="off"
          onChange={(e) => setTyped(e.target.value)}
        />
      </div>

      <div className="form__actions">
        <button
          type="button"
          className="btn btn--sm btn--danger"
          disabled={busy || !matches}
          onClick={() => void remove()}
        >
          {busy ? 'Deleting…' : 'Delete permanently'}
        </button>
        <button
          type="button"
          className="btn btn--sm"
          onClick={() => {
            setOpen(false);
            setTyped('');
            setError(null);
          }}
        >
          Cancel
        </button>
      </div>

      {error ? (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
